<template>
    <div class="h-100" :class="showTaskIntro || showBlockIntro ? 'mb-5' : ''">
        <div class="iat-container p-lg-3" v-if="iatTask">
            <iat-intro
                v-if="showTaskIntro"
                :text="taskIntroText"
            />
            <iat-block-intro
                v-if="showBlockIntro"
                :text="blockIntroText"
                :categories="categories"
            />
            <iat-item
                class="h-100"
                v-if="!showTaskIntro && !showBlockIntro"
                :categories="categories"
                :word="currentWord"
                :timingError="timingError"
                @answer="handleWordAnswer"
            />
        </div>
    </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import { shuffleArray } from '@/utils/items'
import { IAT_TASK_NAME } from '@/constants/items'
import { ASSETS_URL } from '@/constants/misc'

import ItemsContainerMixin from './mixins/ItemsContainerMixin'
import iatItem from '@/components/items/iatItem'
import iatIntro from '@/components/ui/iatIntro'
import iatBlockIntro from '@/components/ui/iatBlockIntro'

export default {
    name: 'IATContainer',
    components: {
        'iat-item': iatItem,
        'iat-intro': iatIntro,
        'iat-block-intro': iatBlockIntro,
    },
    mixins: [ItemsContainerMixin],
    props: {
        item: {
            type: Object
        },
        interactionInstanceToken: {
            type: String
        },
        nextClicked: {
            type: Boolean
        },
    },
    watch: {
        currentBlockCount() {
            this.showBlockIntro = true
            this.toggleCtrlHidden(false)
            this.$emit('answer', true)
        },
        nextClicked(newVal) {
            if(newVal) {
                this.handleNext()
            }
        }
    },
    data() {
        return {
            showTaskIntro: true,
            showBlockIntro: false,
            // we start with -1 for intro text, then proceed to first block index
            currentBlockCount: -1,
            currentWordCount: 0,
            timeout: null,
            overallTimePassed: 0,
            results: [],
            timingError: false,
        }
    },
    computed: {
        ...mapGetters([
            'iatTask',
            'currentItemNumber',
            'interactionInstanceId'
        ]),
        blocks() {
            return [...this.iatTask.blocks].sort((a, b) => a.blockOrder - b.blockOrder)
        },
        activeBlock() {
            return this.blocks[this.currentBlockCount]
        },
        taskIntroText() {
            return this.$t(this.iatTask.taskIntoTranslationKey, { ASSETS_URL })
        },
        blockIntroText() {
            return {
                top: this.$t(`${this.activeBlock.blockIntroTranslationKey}_TOP`, { ASSETS_URL }),
                // WORKAROUND for not working $te i18n function, to be investigated
                middle: this.$t(`${this.activeBlock.blockIntroTranslationKey}_MIDDLE`, { ASSETS_URL }) !== `${this.activeBlock.blockIntroTranslationKey}_MIDDLE` && this.$t(`${this.activeBlock.blockIntroTranslationKey}_MIDDLE`, { ASSETS_URL }),
                bottom: this.$t(`${this.activeBlock.blockIntroTranslationKey}_BOTTOM`, { ASSETS_URL }),
            }
        },
        currentIntroText() {
            if(this.currentBlockCount === -1) {
                return this.$t(this.iatTask.taskIntoTranslationKey, { ASSETS_URL })
            }
            return this.$t(this.activeBlock.blockIntroTranslationKey, { ASSETS_URL })
        },
        taskWordCount() {
            const wordCount = this.blocks.reduce(
                (acc, currBlock) => acc + currBlock.drawFromCategories.reduce(
                    (acc, currDraw) => currDraw.drawWordCount ? acc + currDraw.drawWordCount : acc + currDraw.category.words.length
                    , 0)
                , 0)

            return wordCount
        },
        itemNoThreshold() {
            return {
                fast: Math.ceil((this.taskWordCount/100) * this.iatTask.thresholdFastAnswerPercentage),
                slow: Math.ceil((this.taskWordCount/100) * this.iatTask.thresholdSlowAnswerPercentage)
            }
        },
        categories() {
            const {
                categoryTopLeft,
                categoryBottomLeft,
                categoryTopRight,
                categoryBottomRight
            } = this.activeBlock

            const categories = {
                topLeft: categoryTopLeft && {
                    title: this.$t(categoryTopLeft.categoryTranslationKey),
                    id: categoryTopLeft.categoryId,
                    isHidden: categoryTopLeft.isHidden,
                    words: this.getWords(categoryTopLeft.categoryId),
                    target: true,
                },
                bottomLeft: categoryBottomLeft && {
                    title: this.$t(categoryBottomLeft.categoryTranslationKey),
                    id: categoryBottomLeft.categoryId,
                    isHidden: categoryBottomLeft.isHidden,
                    words: this.getWords(categoryBottomLeft.categoryId),
                    target: true,
                },
                topRight: categoryTopRight && {
                    title: this.$t(categoryTopRight.categoryTranslationKey),
                    id: categoryTopRight.categoryId,
                    isHidden: categoryTopRight.isHidden,
                    words: this.getWords(categoryTopRight.categoryId),
                    target: true,
                },
                bottomRight: categoryBottomRight && {
                    title: this.$t(categoryBottomRight.categoryTranslationKey),
                    id: categoryBottomRight.categoryId,
                    isHidden: categoryBottomRight.isHidden,
                    words: this.getWords(categoryBottomRight.categoryId),
                    target: true,
                }
            }

            return categories
        },
        currentDrawWords() {
            const words = this.activeBlock.drawFromCategories
                .map(blockDrawCategory => {
                    const randomArray = shuffleArray([...blockDrawCategory.category.words])

                    if(blockDrawCategory.drawWordCount) {
                        randomArray.splice(0, randomArray.length - blockDrawCategory.drawWordCount)
                    }

                    return randomArray.map(word => ({
                        ...word,
                        isTrial: blockDrawCategory.isTrial,
                        blockDrawCategoryId: blockDrawCategory.blockDrawCategoryId,
                        categoryId: blockDrawCategory.category.categoryId
                    }))

                })
                .reduce((acc, x) => acc.concat(x))

            return shuffleArray(words).sort((a, b) => a.isTrial ? -1 : 1)
        },
        currentWord() {
            return this.currentDrawWords[this.currentWordCount]
        }
    },
    methods: {
        ...mapActions([
            'fetchIATTask',
            'answerIATTask',
            'answerItem',
            'setCurrentItemNumber',
            'showDialog'
        ]),
        async finishBlock() {
            if(this.currentBlockCount < this.blocks.length - 1) {
                this.currentWordCount = 0
                this.currentBlockCount += 1
            } else {
                await this.answerIATTask({
                    interactionInstanceId: this.interactionInstanceId,
                    itemId: this.item.itemId,
                    result: { results:
                        this.results.map(result => {
                            return {
                                blockDrawCategoryId: result.blockDrawCategoryId,
                                categoryWordId: result.categoryWordId,
                                correctAnswer: result.answerCorrect,
                                responseTime: result.timeElapsed
                            }
                        })
                    }
                })
                this.answerItem({ itemId: this.item.itemId, interactionInstanceToken: this.interactionInstanceToken, answer: { result: 1 } })
                this.setCurrentItemNumber({ number: this.currentItemNumber + 1})
            }
        },
        timingThresholdReached(answers) {
            const aboveFast = answers.filter(answer => !answer.isTrial && answer.timeElapsed < this.iatTask.thresholdFastAnswerInMilliseconds).length
            const aboveSlow = answers.filter(answer => !answer.isTrial && answer.timeElapsed > this.iatTask.thresholdSlowAnswerInMilliseconds).length

            return aboveFast >= this.itemNoThreshold.fast || aboveSlow >= this.itemNoThreshold.slow
        },
        handleWordAnswer(answer) {
            if(!this.timingError) {
                this.results = [...this.results, answer]

                if(this.timingThresholdReached(this.results)) {
                    // show error message
                    this.showTimingError()
                }
                this.resetTimeout()

                this.currentWordCount += 1
                if(this.currentWordCount === this.currentDrawWords.length) {
                    this.finishBlock()
                }
            }
        },
        showTimingError() {
            this.timingError = true;
            this.showDialog({
                modal: true,
                title: this.$t('INTERACTION_BASE_IAT_ERROR_TITLE'),
                content: this.$t('INTERACTION_BASE_IAT_ERROR_TEXT'),
                onConfirm: this.handleDialogConfirm.bind(this)
            })
        },
        handleDialogConfirm() {
            window.location.reload()
        },
        setTimeoutStart() {
            return setTimeout(this.showTimingError, this.iatTask.maxAnswerTimeInMilliseconds)
        },
        resetTimeout() {
            clearTimeout(this.timeout)
            this.timeout = this.setTimeoutStart()
        },
        toggleCtrlHidden(value) {
            this.$emit('ctrlButtonHidden', value)
        },
        handleNext() {
            if(this.showTaskIntro) {
                this.currentBlockCount += 1
                this.showTaskIntro = false
            } else {
                this.showBlockIntro = false
            }

            this.toggleCtrlHidden(true)
            this.$emit('answered')
        },
        handleKeyNext(e) {
            if(e.keyCode == 32 && (this.showBlockIntro || this.showTaskIntro)) {
                this.handleNext()
            }
        },
        getWords(categoryId) {
            return [
                ...new Set(
                    this.currentDrawWords
                        .filter(drawWord => drawWord.categoryId === categoryId)
                        .map(drawWord => this.$t(drawWord.wordTranslationKey))
                )
            ]
        }
    },
    async mounted() {
        const taskName = this.itemProperties.getProperty(IAT_TASK_NAME)
        await this.fetchIATTask({ taskName });

        this.keyListener = window.addEventListener('keydown', this.handleKeyNext)


        this.timeout = this.setTimeoutStart();

        this.showTaskIntro = true;
        this.toggleCtrlHidden(false);
        this.$emit('answer', true);
    },
    beforeDestroy() {
        window.removeEventListener('keydown', this.handleKeyNext)
    }
}
</script>
<style lang="scss">
.iat-container {
    min-height: 200px;
    height: 100%;
    @media (min-width: 992px) {
        background: #F9FDFC;
        height: 85%;
        min-height: 500px;
        -webkit-box-shadow: -2px 4px 11px -1px rgba(201,201,201,0.5);
        -moz-box-shadow: -2px 4px 11px -1px rgba(201,201,201,0.5);
        box-shadow: -2px 4px 11px -1px rgba(201,201,201,0.5);
    }


}
</style>
